import { minutesToMilliseconds } from 'date-fns';
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import {
  createQuestion,
  createQuestionTemplate,
  deleteQuestion,
  fetchQuestion,
  fetchQuestionTemplates,
  fetchQuestionsForSurvey,
  getQuestionResults,
  reorderQuestions,
  toggleQuestionActivation,
  updateQuestion,
} from 'services/backend/questions';
import { ReactQueryFunctionCallbacks } from 'types/internal';
import { Question } from 'types/domainModels';
import { questionBlockQueries } from './questionBlocks';
import { surveyQueries } from './surveys';

export const questionQueries = {
  all: () => ['questions'],
  results: (opts: { questionId: number }) => [
    ...questionQueries.all(),
    opts.questionId,
    'results',
  ],
  resultsV2: (opts: { questionId: number; waveIds: number[] }) => [
    ...questionQueries.all(),
    opts.questionId,
    'results-v2',
    opts.waveIds,
  ],
  forSurvey: (opts: { surveyId: number }) =>
    queryOptions({
      queryFn: () => fetchQuestionsForSurvey({ surveyId: opts.surveyId }),
      queryKey: [...questionQueries.all(), 'surveys', opts.surveyId],
    }),
  get: (opts: { questionId: number }) =>
    queryOptions({
      queryFn: () => fetchQuestion({ questionId: opts.questionId }),
      queryKey: [...questionQueries.all(), opts.questionId],
      staleTime: minutesToMilliseconds(0.5),
    }),
  getResults: (opts: { questionId: number; waveIds: number[] }) =>
    queryOptions({
      queryFn: () =>
        getQuestionResults({
          data: { waveIds: opts.waveIds },
          questionId: opts.questionId,
        }),
      queryKey: [
        ...questionQueries.resultsV2({
          questionId: opts.questionId,
          waveIds: opts.waveIds,
        }),
      ],
      staleTime: minutesToMilliseconds(0.5),
    }),
  listTemplates: (opts: { organizationId?: number } = {}) =>
    queryOptions({
      queryFn: () =>
        fetchQuestionTemplates({ organizationId: opts.organizationId }),
      queryKey: [
        ...questionQueries.all(),
        'question-templates',
        opts.organizationId,
      ],
      staleTime: minutesToMilliseconds(5),
    }),
};

export function useCreateQuestionTemplate(
  opts?: ReactQueryFunctionCallbacks<typeof createQuestionTemplate>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: createQuestionTemplate,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: [...questionQueries.all(), 'question-templates'],
      });

      opts?.onSuccess?.(...args);
    },
  });
}

export function useDeleteQuestion(
  opts?: ReactQueryFunctionCallbacks<typeof deleteQuestion>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: async ({
      surveyId,
      ...rest
    }: Parameters<typeof deleteQuestion>[0] & { surveyId: number }) => {
      const deleteQuestionResult = await deleteQuestion(rest);

      await queryClient.invalidateQueries(surveyQueries.survey({ surveyId }), {
        throwOnError: true,
      });
      await queryClient.invalidateQueries(
        questionQueries.forSurvey({ surveyId }),
      );

      return deleteQuestionResult;
    },
  });
}

export function useReorderQuestions(
  opts?: ReactQueryFunctionCallbacks<typeof reorderQuestions>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: reorderQuestions,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(
        questionQueries.forSurvey({ surveyId: variables.surveyId }),
      );

      // Snapshot the previous value
      const previousQuestions = queryClient.getQueryData(
        questionQueries.forSurvey({ surveyId: variables.surveyId }).queryKey,
      );

      // Optimistically update to the new value
      queryClient.setQueryData(
        questionQueries.forSurvey({ surveyId: variables.surveyId }).queryKey,
        (old: Question[] | undefined) => {
          if (!old) {
            return old;
          }

          // Create a new array with updated sort orders
          const newQuestions = [...old];
          variables.data.forEach((updatedQuestion) => {
            const index = newQuestions.findIndex(
              (q) => q.id === updatedQuestion.id,
            );
            if (index !== -1) {
              newQuestions[index] = {
                ...newQuestions[index],
                sort: updatedQuestion.sort,
                blockId: updatedQuestion.blockId,
                monadicId: updatedQuestion.monadicId ?? undefined,
              };
            }
          });

          // Sort by the new sort order
          return newQuestions.sort((a, b) => a.sort - b.sort);
        },
      );

      // Return a context object with the snapshotted value
      return { previousQuestions };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousQuestions) {
        queryClient.setQueryData(
          questionQueries.forSurvey({ surveyId: variables.surveyId }).queryKey,
          context.previousQuestions,
        );
      }

      opts?.onError?.(err, variables, context);
    },
    onSuccess: (...args) => {
      const surveyId = args[1].surveyId;

      queryClient.invalidateQueries(questionQueries.forSurvey({ surveyId }));
      queryClient.invalidateQueries(questionBlockQueries.list({ surveyId }));

      opts?.onSuccess?.(...args);
    },
    onSettled: () => {
      opts?.onSettled?.();
    },
  });
}

export function useSaveQuestion(
  opts?: ReactQueryFunctionCallbacks<
    typeof createQuestion | typeof updateQuestion
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: async (data) => {
      const saveQuestionResult = await ('questionId' in data
        ? updateQuestion(data)
        : createQuestion(data));

      const surveyId = saveQuestionResult.surveyId;

      await queryClient.invalidateQueries(surveyQueries.survey({ surveyId }), {
        throwOnError: true,
      });
      await queryClient.invalidateQueries(
        questionQueries.forSurvey({ surveyId }),
        { throwOnError: true },
      );

      return saveQuestionResult;
    },
  });
}

export function useToggleQuestionActivation(
  opts?: ReactQueryFunctionCallbacks<typeof toggleQuestionActivation>,
) {
  return useMutation({ ...opts, mutationFn: toggleQuestionActivation });
}
